import React, { useState, useEffect, useRef} from 'react';

import ExtendedRecipeListingList from './components/ExtendedRecipeListingList';
import ExtendedRecipeListingToolbar from './components/ExtendedRecipeListingToolbar';
import ExtendedRecipeListingSidebar from './components/ExtendedRecipeListingSidebar';
import ExtendedRecipeListingMobileFilter from './components/ExtendedRecipeListingMobileFilter';
import './recipe-listing.scss';

export default function ExtendedRecipeListing  ({settings}) {
    const { all_recipes, yotpo_snippet, translations, promotion_blocks, color_schema, items_per_grid, 
      theme_filter, season_filter, meal_filter,  meat_filter, fish_filter, grains_filter, vegetables_filter, fruit_filter, courses_filter, collections_filter, features_filter, baking_filter, diet_filter, occasions_filter, wishlist } = settings;

    const staticEmptyState = {
        theme: theme_filter ? [...theme_filter] : [],
        meal: meal_filter ? [...meal_filter] : [],
        season: season_filter ? [...season_filter] : [],
        meat: meat_filter ? [...meat_filter] : [],
        fish_seafood: fish_filter ? [...fish_filter] : [],
        grains_pulses: grains_filter ? [...grains_filter] : [],
        vegetables: vegetables_filter ? [...vegetables_filter] : [],
        fruit: fruit_filter ? [...fruit_filter] : [],
        courses: courses_filter ? [...courses_filter] : [],
        collections: collections_filter ? [...collections_filter] : [],
        features: features_filter ? [...features_filter] : [],
        baking: baking_filter ? [...baking_filter] : [],
        by_diet: diet_filter ? [...diet_filter] : [],
        by_occasions: occasions_filter ? [...occasions_filter] : []
    };

    const emptyFilterState = Object.keys(all_recipes[0]?.filters || {}).reduce((acc, key) => {
        if (!(key in staticEmptyState)) {
            const filterKey = `${key}_filter` as keyof typeof settings;
            acc[key] = settings[filterKey] ? [...settings[filterKey] as string[]] : [];
        }
        return acc;
    }, {...staticEmptyState} as Record<string, string[]>);

    const [ selectedFilters, setSelectedFilters ] = useState(emptyFilterState);
    const [ selectedSort, setSelectedSort ] = useState("default");
    const [ gridItems, setGridItems ] = useState([]);
    const [ itemCount, setItemCount ] = useState(items_per_grid);
    const [  maxCount, setMaxCount ] = useState(0);

    const promoBlocks = useRef(promotion_blocks);

    const SORT_OPTIONS = [
        { value: "default", label: `${translations.sort_default}` },
        { value: "ascending", label: `${translations.sort_ascending}` },
        { value: "descending", label: `${translations.sort_descending}` }
    ]
  
    type FilterSet = Set<string>;
    type FilterSets = Record<string, FilterSet>;
    
    const filterSets: FilterSets = {};
    

    all_recipes.forEach(item => {
        Object.keys(item.filters).forEach(filterKey => {
            if (!filterSets[filterKey]) {
                filterSets[filterKey] = new Set();
            }
            item.filters[filterKey].forEach(filter => filterSets[filterKey].add(filter));
        });
    });


    const filterArrays = Object.fromEntries(
        Object.entries(filterSets).map(([key, set]) => [key, Array.from(set)])
    );

   
    const filterInfo = all_recipes[0]?.filters ? Object.keys(all_recipes[0].filters).map(key => ({
        key: key,
        label: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    })) : [];


    const FILTERS_LIST = filterInfo.map(({ key, label }) => {
        const correspondingFilter = `${key}_filter` as keyof typeof settings;
        return {
            filter: key,
            label: label,
            options: filterArrays[key]?.filter(item => !settings[correspondingFilter]?.includes(item)) || []
        };
    });

    const filteredRecipes = all_recipes.filter(item => {
        const hasFilters = Object.values(selectedFilters as Record<string, string[]>)
            .some(filters => filters.length > 0);
        
        if (!hasFilters) return true;

        return Object.entries(selectedFilters as Record<string, string[]>)
            .every(([filterKey, selectedValues]) => {
                if (selectedValues.length === 0) return true;
                
                const itemFilters = (item.filters[filterKey] || []) as string[];
                return itemFilters.some(filter => selectedValues.includes(filter));
            });
    })


    const sortedRecipes = filteredRecipes.sort((a, b) => {
        if (selectedSort == 'descending') {
            return(b.title.localeCompare(a.title))
        }
        return(a.title.localeCompare(b.title))
    } );

    const recipeResults = selectedSort == 'default' ? filteredRecipes : sortedRecipes;


    const getPromoBlocksToBeInserted = (supposedToBeCurrentlyDisplayed) => {
        const blocksForNextPage = [];
    
        promoBlocks.current.forEach(block => {
          block.position.sort((a, b) => a - b);
          block.position.forEach(position => {
            if (position <= supposedToBeCurrentlyDisplayed) {
              blocksForNextPage.push({
                position: position - 1,
                block
              });
            }
          });
        });
        return blocksForNextPage;
      };
  
    const insertPromoBlocks = (recipesToBeAdded, promoBlocksForNextPage) => {
        let recipesCopy = recipesToBeAdded.concat([]);
        promoBlocksForNextPage.forEach(({ position, block }) => {
          recipesCopy.splice(position, 0, block);
        });
    
        return recipesCopy;
      };
    
      useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const queryObject: Record<string, string[]> = { ...emptyFilterState };
        

        const availableFilters = Object.keys(all_recipes[0]?.filters || {});
    
        searchParams.forEach((value, key) => {
          const parseKey = key && key.split('-')[1];
          const currVal = value.split(',');
          

          if (parseKey && availableFilters.includes(parseKey)) {
            const baseFilter = emptyFilterState[parseKey as keyof typeof emptyFilterState] || [];
            queryObject[parseKey] = [...baseFilter, ...currVal];
          }
        });

        setSelectedFilters(queryObject);
      }, [all_recipes]); 

    useEffect(() => {

        const convertToUrlParams = (filters: Record<string, string[]>) => {
            const queryParams = Object.entries(filters)
            .filter(([_key, values]) => values.length > 0)
            .map(([key, values]) => `filter-${key}=${encodeURIComponent(values.join(','))}`) 
            .join('&');
        
            return queryParams;
        };


        const filteredSelectedFilters = Object.keys(selectedFilters).reduce((acc, key) => {
          const filterKey = key as keyof typeof selectedFilters;
          const baseFilterKey = `${key}_filter` as keyof typeof settings;
          const baseFilter = settings[baseFilterKey] || [];
          
          acc[filterKey] = selectedFilters[filterKey].filter(
            (item: string) => !baseFilter.includes(item)
          );
          
          return acc;
        }, {} as Record<string, string[]>);
      
        const urlParams = convertToUrlParams(filteredSelectedFilters);
        if (urlParams != '') window.history.pushState(null, '', `?${urlParams}`);
        else  { 
            const newUrl = `${window.location.pathname}`;
            window.history.replaceState(null, '', newUrl);
        }  

        const promoBlocksItems = insertPromoBlocks(recipeResults, getPromoBlocksToBeInserted(itemCount))
        setMaxCount(promoBlocksItems.length);
        setGridItems( promoBlocksItems.slice(0, itemCount));
        
    }, [selectedFilters, itemCount, selectedSort]);

 
    const createFilteredObject = (filters: Record<string, string[]>) => {
        return Object.entries(filters).reduce((acc, [key, values]) => {
            const baseFilterKey = `${key}_filter` as keyof typeof settings;
            const baseFilter = (settings[baseFilterKey] || []) as string[];
            
            acc[key] = values.filter(item => !baseFilter.includes(item));
            return acc;
        }, {} as Record<string, string[]>);
    };


    const toolBarFilters = createFilteredObject(selectedFilters);

    
    return (
        <div className="recipe-listing container">
            <ExtendedRecipeListingMobileFilter translations={translations} filters={FILTERS_LIST} color_schema={color_schema} selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
            <ExtendedRecipeListingToolbar sortOptions={SORT_OPTIONS} emptyFilterState={emptyFilterState} recipes={gridItems} selectedFilters={toolBarFilters} setSelectedFilters={setSelectedFilters}  selectedSort={selectedSort} setSelectedSort={setSelectedSort} translations={translations}  recipesCount={recipeResults.length}/>

            <div className="recipe-listing__content">
                <ExtendedRecipeListingSidebar selectedFilters={selectedFilters}  setSelectedFilters={setSelectedFilters} filters={FILTERS_LIST} translations={translations} />
                <ExtendedRecipeListingList recipes={gridItems} yotpo_snippet={yotpo_snippet} translations={translations} maxCount={maxCount} setItemCount={setItemCount} itemCount={itemCount} loadCount={items_per_grid} recipesCount={recipeResults.length} wishlist={wishlist}/>
            </div>
        
        </div>
    );
   
  };



